import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Plus, Stethoscope, AlertTriangle } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

export default function TriajePage() {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [imc, setImc] = useState(null);
  const [imcCategory, setImcCategory] = useState('');
  
  const [formData, setFormData] = useState({
    patient_id: '',
    presion_arterial: '',
    frecuencia_cardiaca: '',
    frecuencia_respiratoria: '',
    temperatura: '',
    saturacion_oxigeno: '',
    peso: '',
    talla: '',
    motivo_consulta: '',
    sintomas_principales: '',
    observaciones: '',
    prioridad: 'normal'
  });

  useEffect(() => {
    fetchPatients();
  }, []);

  useEffect(() => {
    if (formData.peso && formData.talla) {
      const weight = parseFloat(formData.peso);
      const height = parseFloat(formData.talla) / 100;
      if (weight > 0 && height > 0) {
        const calculatedImc = (weight / (height * height)).toFixed(2);
        setImc(calculatedImc);
        
        if (calculatedImc < 18.5) setImcCategory('Bajo peso');
        else if (calculatedImc < 25) setImcCategory('Normal');
        else if (calculatedImc < 30) setImcCategory('Sobrepeso');
        else setImcCategory('Obesidad');
      }
    }
  }, [formData.peso, formData.talla]);

  const fetchPatients = async () => {
    try {
      const response = await api.get('/patients');
      setPatients(response.data);
    } catch (error) {
      toast.error('Error al cargar pacientes');
    } finally {
      setLoading(false);
    }
  };

  const getVitalSignColor = (type, value) => {
    const val = parseFloat(value);
    if (!val) return 'border-gray-300';
    
    switch(type) {
      case 'frecuencia_respiratoria':
        return (val > 20 || val < 12) ? 'border-red-500 border-2' : 'border-green-500 border-2';
      case 'frecuencia_cardiaca':
        return (val > 100 || val < 60) ? 'border-red-500 border-2' : 'border-green-500 border-2';
      case 'saturacion_oxigeno':
        return val < 94 ? 'border-red-500 border-2' : 'border-green-500 border-2';
      case 'presion_arterial':
        const [systolic, diastolic] = value.split('/').map(v => parseInt(v));
        if (systolic > 140 || diastolic > 90) return 'border-red-500 border-2';
        return 'border-green-500 border-2';
      default:
        return 'border-gray-300';
    }
  };

  const getPriorityColor = (priority) => {
    switch(priority) {
      case 'muy_urgente': return 'bg-red-600';
      case 'urgente': return 'bg-orange-500';
      case 'normal': return 'bg-green-500';
      case 'no_urgente': return 'bg-blue-500';
      default: return 'bg-gray-500';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post('/triaje', formData);
      toast.success('Triaje registrado exitosamente');
      setDialogOpen(false);
      // Reset form
      setFormData({
        patient_id: '',
        presion_arterial: '',
        frecuencia_cardiaca: '',
        frecuencia_respiratoria: '',
        temperatura: '',
        saturacion_oxigeno: '',
        peso: '',
        talla: '',
        motivo_consulta: '',
        sintomas_principales: '',
        observaciones: '',
        prioridad: 'normal'
      });
      setImc(null);
      setImcCategory('');
    } catch (error) {
      console.error('Error creating triaje:', error);
      toast.error(error.response?.data?.detail || 'Error al crear triaje');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="triaje-title">
            Triaje de Enfermería
          </h1>
          <p className="text-muted-foreground mt-2">
            Evaluación inicial de pacientes con priorización
          </p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-teal-600 hover:bg-teal-700" data-testid="create-triaje-button">
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Triaje
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="create-triaje-dialog">
            <DialogHeader>
              <DialogTitle>Realizar Triaje de Enfermería</DialogTitle>
              <DialogDescription>
                Complete la evaluación inicial del paciente
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="patient_id">Paciente *</Label>
                <Select value={formData.patient_id} onValueChange={(value) => setFormData({ ...formData, patient_id: value })} required>
                  <SelectTrigger data-testid="patient-select">
                    <SelectValue placeholder="Seleccionar paciente..." />
                  </SelectTrigger>
                  <SelectContent>
                    {patients.map((patient) => (
                      <SelectItem key={patient.id} value={patient.id}>
                        {patient.first_name} {patient.last_name} - DNI: {patient.dni}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Card className="p-4 bg-teal-50">
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <Stethoscope className="w-5 h-5" />
                  Signos Vitales
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <Label>Presión Arterial *</Label>
                    <Input
                      placeholder="120/80"
                      value={formData.presion_arterial}
                      onChange={(e) => setFormData({...formData, presion_arterial: e.target.value})}
                      className={getVitalSignColor('presion_arterial', formData.presion_arterial)}
                      required
                    />
                  </div>
                  <div>
                    <Label>FC (lpm) *</Label>
                    <Input
                      type="number"
                      placeholder="70"
                      value={formData.frecuencia_cardiaca}
                      onChange={(e) => setFormData({...formData, frecuencia_cardiaca: e.target.value})}
                      className={getVitalSignColor('frecuencia_cardiaca', formData.frecuencia_cardiaca)}
                      required
                    />
                  </div>
                  <div>
                    <Label>FR (rpm) *</Label>
                    <Input
                      type="number"
                      placeholder="16"
                      value={formData.frecuencia_respiratoria}
                      onChange={(e) => setFormData({...formData, frecuencia_respiratoria: e.target.value})}
                      className={getVitalSignColor('frecuencia_respiratoria', formData.frecuencia_respiratoria)}
                      required
                    />
                  </div>
                  <div>
                    <Label>SatO2 (%) *</Label>
                    <Input
                      type="number"
                      placeholder="98"
                      value={formData.saturacion_oxigeno}
                      onChange={(e) => setFormData({...formData, saturacion_oxigeno: e.target.value})}
                      className={getVitalSignColor('saturacion_oxigeno', formData.saturacion_oxigeno)}
                      required
                    />
                  </div>
                  <div>
                    <Label>Temperatura (°C) *</Label>
                    <Input
                      type="number"
                      step="0.1"
                      placeholder="36.5"
                      value={formData.temperatura}
                      onChange={(e) => setFormData({...formData, temperatura: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label>Peso (kg) *</Label>
                    <Input
                      type="number"
                      step="0.1"
                      placeholder="70"
                      value={formData.peso}
                      onChange={(e) => setFormData({...formData, peso: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label>Talla (cm) *</Label>
                    <Input
                      type="number"
                      placeholder="170"
                      value={formData.talla}
                      onChange={(e) => setFormData({...formData, talla: e.target.value})}
                      required
                    />
                  </div>
                  {imc && (
                    <div className="flex flex-col justify-center">
                      <Label>IMC Calculado</Label>
                      <p className="text-2xl font-bold text-teal-600">{imc}</p>
                      <p className="text-xs text-muted-foreground">{imcCategory}</p>
                    </div>
                  )}
                </div>
                <div className="mt-3 p-2 bg-white rounded text-xs">
                  <p className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-600" />
                    Valores con <span className="font-bold text-red-600">BORDE ROJO</span> requieren atención
                  </p>
                </div>
              </Card>

              <div className="space-y-2">
                <Label htmlFor="motivo_consulta">Motivo de Consulta *</Label>
                <Textarea
                  id="motivo_consulta"
                  value={formData.motivo_consulta}
                  onChange={(e) => setFormData({ ...formData, motivo_consulta: e.target.value })}
                  required
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="sintomas_principales">Síntomas Principales</Label>
                <Textarea
                  id="sintomas_principales"
                  value={formData.sintomas_principales}
                  onChange={(e) => setFormData({ ...formData, sintomas_principales: e.target.value })}
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="observaciones">Observaciones de Enfermería</Label>
                <Textarea
                  id="observaciones"
                  value={formData.observaciones}
                  onChange={(e) => setFormData({ ...formData, observaciones: e.target.value })}
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="prioridad">Clasificación de Prioridad *</Label>
                <Select value={formData.prioridad} onValueChange={(value) => setFormData({ ...formData, prioridad: value })} required>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="muy_urgente">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-red-600"></div>
                        Muy Urgente (Rojo)
                      </div>
                    </SelectItem>
                    <SelectItem value="urgente">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                        Urgente (Naranja)
                      </div>
                    </SelectItem>
                    <SelectItem value="normal">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-green-500"></div>
                        Normal (Verde)
                      </div>
                    </SelectItem>
                    <SelectItem value="no_urgente">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                        No Urgente (Azul)
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-teal-600 hover:bg-teal-700">
                  Guardar Triaje
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="p-6">
        <div className="flex flex-col items-center justify-center py-12">
          <Stethoscope className="w-16 h-16 text-muted-foreground mb-4" />
          <p className="text-muted-foreground text-center">
            No hay triajes registrados hoy
          </p>
          <p className="text-sm text-muted-foreground text-center mt-2">
            Los triajes realizados aparecerán aquí
          </p>
        </div>
      </Card>
    </div>
  );
}
